import { Box, Button, Flex, Select, Spinner, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tooltip, Tr } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import {
  STATUS_APPROVED,
  STATUS_REJECTED
} from "src/common/constants/statusConstants";

import { AdminService } from "src/rest/adminService";
import { Admin_Labels_Eng } from "src/common/constants/englishLabels";
import { Admin_Labels_Hindi } from "src/common/constants/hindiLabels";
import { BUTTONS_ENG } from "src/common/constants/englishLabels";
import { RootState } from "src/store/store";
import { useSelector } from "react-redux";

type UserAction = {
  userEmail: string;
  adminEmail: string;
  action: string; // approved/rejected/pending
  reason?: string | null;
  timestamp?: string | null;
};

export const HistoryTable: React.FC = () => {
  const [rows, setRows] = useState<UserAction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<"ALL" | typeof STATUS_APPROVED | typeof STATUS_REJECTED>("ALL");
  const pageSize = 10;

  const globalInfo = useSelector((state: RootState) => state.globalInfo);
      const { globalLanguage } = globalInfo;
      const isHindiLabels = globalLanguage === 'HI';

  const fetchHistory = async (s: "ALL" | typeof STATUS_APPROVED | typeof STATUS_REJECTED) => {
    try {
      setLoading(true);
      setError(null);
      const res = await AdminService.getUserActionHistory(s);
      const raw = Array.isArray(res.data) ? res.data : [res.data];
      const seen = new Set<string>();
      const unique: UserAction[] = [];
      raw.forEach((r: any) => {
        const key = `${r.userEmail}|${r.adminEmail}|${r.action}|${r.timestamp ?? ""}`;
        if (!seen.has(key)) {
          seen.add(key);
          unique.push(r);
        }
      });
      setRows(unique);
      setPage(1);
    } catch (e: any) {
      setError("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory(status);
  }, [status]);

  // Decide slice based on mode
  const displayRows = rows.slice((page - 1) * pageSize, page * pageSize);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));

  return (
    <Box bg="#FFFFFF" borderRadius="16px" border="1px solid #EDEDED" p={6}>
      <Flex align="center" justify="space-between" mb={3}>
        <Text fontWeight="bold" color="#2C2C2C">{isHindiLabels? Admin_Labels_Hindi.User_Approval_History: Admin_Labels_Eng.User_Approval_History}</Text>
        <Flex align="center" gap={3}>
          <Select size="sm" value={status} onChange={(e) => setStatus(e.target.value as any)} width="160px">
            <option value="ALL">{isHindiLabels ? Admin_Labels_Hindi.ALL : Admin_Labels_Eng.ALL}</option>
            <option value={STATUS_APPROVED}>{isHindiLabels ? Admin_Labels_Hindi.APPROVE : Admin_Labels_Eng.APPROVE}</option>
            <option value={STATUS_REJECTED}>{isHindiLabels ? Admin_Labels_Hindi.REJECT : Admin_Labels_Eng.REJECT}</option>
          </Select>
        </Flex>
      </Flex>

      {loading ? (
        <Flex align="center" justify="center" py={6}><Spinner /></Flex>
      ) : error ? (
        <Text color="red.500" fontSize="sm">{error}</Text>
      ) : (
        <>
          <TableContainer border="1px" borderColor="#EBEBEB" mt={3}>
            <Table variant="simple">
              <Thead backgroundColor="#F4F4F4">
                <Tr>
                  <Th paddingLeft="12px" paddingRight="12px" paddingTop="20px" paddingBottom="20px" color="#6B7280">{isHindiLabels ? Admin_Labels_Hindi.EMAIL : Admin_Labels_Eng.EMAIL}</Th>
                  <Th paddingLeft="12px" paddingRight="12px" paddingTop="20px" paddingBottom="20px" color="#6B7280">{isHindiLabels ? Admin_Labels_Hindi.ACTION : Admin_Labels_Eng.ACTION}</Th>
                  <Th paddingLeft="12px" paddingRight="12px" paddingTop="20px" paddingBottom="20px" color="#6B7280">{isHindiLabels ? Admin_Labels_Hindi.ADMIN_EMAIL : Admin_Labels_Eng.ADMIN_EMAIL}</Th>
                  <Th paddingLeft="12px" paddingRight="12px" paddingTop="20px" paddingBottom="20px" color="#6B7280">{isHindiLabels ? Admin_Labels_Hindi.REASON : Admin_Labels_Eng.REASON}</Th>
                  <Th paddingLeft="12px" paddingRight="12px" paddingTop="20px" paddingBottom="20px" color="#6B7280">{isHindiLabels ? Admin_Labels_Hindi.TIMESTAMP : Admin_Labels_Eng.TIMESTAMP}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {displayRows.length === 0 ? (
                  <Tr><Td colSpan={5} paddingLeft="12px" paddingRight="12px" paddingTop="20px" paddingBottom="20px"><Text color="#b4b9bd">{isHindiLabels ? Admin_Labels_Hindi.NO_HISTORY_FOUND : Admin_Labels_Eng.NO_HISTORY_FOUND}</Text></Td></Tr>
                ) : (
                  displayRows.map((r, i) => (
                    <Tr key={`${r.userEmail}-${i}`} borderBottom="1px solid #EFEFEF">
                      <Td paddingLeft="12px" paddingRight="12px" paddingTop="20px" paddingBottom="20px">{r.userEmail}</Td>
                      <Td paddingLeft="12px" paddingRight="12px" paddingTop="20px" paddingBottom="20px" textTransform="capitalize">{r.action || "—"}</Td>
                      <Td paddingLeft="12px" paddingRight="12px" paddingTop="20px" paddingBottom="20px">{r.adminEmail || "—"}</Td>
                      <Td paddingLeft="12px" paddingRight="12px" paddingTop="20px" paddingBottom="20px">
                        {r.reason ? (
                          (() => {
                            const reason = r.reason as string;
                            const showTip = reason.length > 50; // show tooltip only when likely truncated
                            const content = (
                              <Text
                                noOfLines={1}
                                isTruncated
                                maxW={{ base: "140px", md: "240px", lg: "320px" }}
                              >
                                {reason}
                              </Text>
                            );
                            return showTip ? (
                              <Tooltip label={reason} hasArrow placement="top">{content}</Tooltip>
                            ) : (
                              content
                            );
                          })()
                        ) : (
                          "—"
                        )}
                      </Td>
                      <Td paddingLeft="12px" paddingRight="12px" paddingTop="20px" paddingBottom="20px">{(r.timestamp || (r as any).timeStamp) ? new Date((r.timestamp || (r as any).timeStamp) as string).toLocaleString() : "—"}</Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </TableContainer>

          {rows.length > pageSize && (
            <Flex mt={3} justify="flex-end" gap={2} align="center">
              <Button size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} isDisabled={page === 1}>{BUTTONS_ENG.PREV} </Button>
              <Text fontSize="sm">Page {page} of {totalPages}</Text>
              <Button size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} isDisabled={page === totalPages}>{BUTTONS_ENG.NEXT}</Button>
            </Flex>
          )}
        </>
      )}
    </Box>
  );
};